import React, { useContext, useEffect } from 'react';
import { Button, Divider, FormControl, FormControlLabel, Radio, RadioGroup, makeStyles } from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { colors } from '@skyslope/mache';
import cs from 'classnames';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { type LaunchDarklyFlags, withLaunchDarkly } from '@/common/launchDarkly';
import {
  DeprecatedModal,
  ModalCancelButton,
  ModalConfirmationButton,
  ModalText,
  ModalTitle,
} from '../../common/DeprecatedModal';
import { useIsSkySlopeMobileApp } from '../../common/utils';
import { HeaderContext } from '../../context/header-context';
import { useExtendTrial, useGetProductPriceInfo } from '../../hooks/accounts-api';
import { useDS3Access } from '../../hooks/trial/use-ds3-access';
import { useBreakPointObserver } from '../../hooks/useBreakPointObserver';
import { ReactComponent as LockOutImage } from '../../images/lockoutkey.svg';
import * as accountApi from '../../lib/api/account/accountApi';
import * as actions from '../../store/pageFrame/actions';
import { setLoading, updateUserSubscription } from '../../store/pageFrame/actions';
import type { ICheckoutBody } from '../../store/templateCreate/template-types';
import { Spinner } from '../common';

const useStyles = makeStyles(() => ({
  checked: {
    color: `${colors.blue[800]} !important`,
  },
  containerMobile: (prop: { headerHeight: number }) => ({
    overflowY: 'auto',
    width: '100vw',
    paddingTop: `${prop.headerHeight + 40}px`,
    marginTop: '0px',
    paddingBottom: '206px', // enough padding to make sure the button is not covered by the bottom menu and button
    height: `calc(100vh - ${prop.headerHeight}px - 184px)`, // 72px is the height of the button + 112px extra for iphones
  }),
}));

const breakPoints = {
  mobile: '(max-width:579px)',
  desktop: '(min-width:580px)',
};

interface LocationState {
  url?: string;
}

function SubscriptionPage(props: { flags: LaunchDarklyFlags }) {
  const { flags } = props;
  const dispatch = useDispatch();
  const extendFreeTrial = useExtendTrial();
  const history = useHistory<LocationState>();
  const isMobileApp = useIsSkySlopeMobileApp();
  const classes = useStyles({ headerHeight: 73 });
  const headerContext = useContext(HeaderContext);
  const { isEligibleForExtension } = useDS3Access(flags);
  const breakPoint = useBreakPointObserver(breakPoints);
  const { data: productPriceInfo } = useGetProductPriceInfo();

  const params = new URLSearchParams(window.location.search);
  const plan = params.get('p');
  const coupon = params.get('c');
  const directToCheckout = coupon && plan;
  const isSmallScreen = breakPoint === 'mobile';
  const isMobileAppOrSmallScreen = isMobileApp || isSmallScreen;

  useEffect(() => {
    headerContext.setContent(<div />);
    if (directToCheckout) {
      dispatch(setLoading(true));
      handlePaymentRedirect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, []);

  async function handlePaymentRedirect() {
    // if no url is passed in state then redirect to envelope managment
    const body: ICheckoutBody = {
      redirectUrl: history.location?.state?.url ?? `http://${window.location.host}/envelopes`,
      priceId: productPriceInfo?.price?.priceId ?? '',
    };
    if (coupon) body.coupon = coupon;
    // get the url to navigate to Stripe
    const res = await accountApi.checkout(body);
    if (res.status !== 200) {
      dispatch(actions.errorToast('Unable to continue to payments page. Please try again.'));
      dispatch(setLoading(false));
      throw Error(`There was an error in the Stripe Checkout: ${res.status} `);
    }

    window.location.replace(res.result.url);
  }

  async function handleTrialExtensionClick() {
    await extendFreeTrial.mutateAsync();
    const redirectUrl = history.location?.state?.url ?? `http://${window.location.host}/envelopes`;
    window.location.replace(redirectUrl);
  }

  function handleAlreadyPaidClick() {
    dispatch(updateUserSubscription());
  }

  function LockOutBackDrop() {
    return (
      <div className="w-full bg-grey-100">
        <LockOutImage className="absolute right-[10%] top-[12%]" />
      </div>
    );
  }

  const content = (
    <div id="paymentContainer">
      <div className="mt-2.5 mb-8 mx-0">
        <ModalText>
          Want more? It's not too late&mdash;start a DigiSign subscription to unlock all the bells and whistles. In the
          meantime, you can still access your existing complete DigiSign envelopes.
        </ModalText>
      </div>

      <FormControl className="w-full" component="fieldset">
        <RadioGroup
          className="flex-row justify-around pt-4 pb-0 mx-0 min-h-[140px]"
          aria-label="price"
          name="price"
          value={productPriceInfo?.price?.priceId}
          data-testid="price_options"
        >
          <FormControlLabel
            className="flex-row-reverse min-h-[100px] rounded-[5px] justify-between pl-4 mb-3 mx-0 w-full lex-1 m-[5px] px-2.5 py-0 border-[thin] text-grey-700  border-grey-700 border-solid"
            value={productPriceInfo?.price?.priceId}
            data-testid="option-1"
            checked
            control={<Radio classes={{ checked: classes.checked }} color="primary" />}
            label={
              <div>
                <strong>{productPriceInfo?.productName}</strong>
                <br />
                {productPriceInfo?.price?.description}
              </div>
            }
          />
          <Divider className="hidden sm:block" orientation="vertical" variant="middle" flexItem />
        </RadioGroup>
      </FormControl>
    </div>
  );
  const actionButtons = !isMobileAppOrSmallScreen ? (
    <>
      {isEligibleForExtension ? (
        <ModalCancelButton variant="text" onClick={handleTrialExtensionClick} aria-label="keep envelope">
          Extend Free Trial
        </ModalCancelButton>
      ) : (
        <ModalCancelButton variant="outlined" onClick={handleAlreadyPaidClick} aria-label="keep envelope">
          I've Already Paid
        </ModalCancelButton>
      )}
      <ModalConfirmationButton
        onClick={handlePaymentRedirect}
        aria-label="confirm cancel envelope"
        disabled={!productPriceInfo?.price?.priceId}
      >
        Unlock Access
      </ModalConfirmationButton>
    </>
  ) : (
    <Button
      className={cs(
        'fixed w-full min-h-[72px] m-0 p-0 bottom-0 left-0 rounded-none hover:brightness-[85%] active:brightness-[80%]',
        productPriceInfo?.price?.priceId && 'bg-gradient-to-r from-blue-800 to-blue-400 bg-transparent'
      )}
      color="primary"
      variant="contained"
      onClick={handlePaymentRedirect}
      endIcon={<ChevronRightIcon />}
      data-spec="modal-confirmation-button"
      disabled={!productPriceInfo?.price?.priceId}
    >
      Unlock Access
    </Button>
  );

  if (!productPriceInfo?.price) return <Spinner />;

  return (
    <div>
      {directToCheckout ? null : (
        <DeprecatedModal
          modalStyles={{
            position: 'absolute',
            left: '0%',
            top: '0',
            padding: isMobileAppOrSmallScreen ? '0' : '40px 20px 0px',
            maxWidth: '560px',
            minWidth: '336px',
            maxHeight: 'none',
            boxShadow: 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
            actionButtonsStyle: {
              paddingBottom: '40px',
              paddingLeft: '40px',
            },
            margin: isMobileAppOrSmallScreen ? '0 0 0 0' : '10% 17%',
            titlePadding: isMobileAppOrSmallScreen ? '0px 24px 15px' : '',
            modalContentStyle: isMobileAppOrSmallScreen ? { padding: '0 12px' } : {},
          }}
          hideCloseButton
          title={<ModalTitle>Unlock full access to DigiSign 🚀</ModalTitle>}
          content={content}
          actionButtons={actionButtons}
          backdropComponent={LockOutBackDrop}
          modalContainerClassName={isMobileAppOrSmallScreen ? classes.containerMobile : ''}
          dialogClassName={`${isMobileAppOrSmallScreen ? 'z-[1]' : 'bg-grey-100 overflow-y-auto'}`}
        />
      )}
    </div>
  );
}

const withLd = withLaunchDarkly(SubscriptionPage);
export { withLd as SubscriptionPage };
